/**
 * ONBOARDING STATUS UPDATE
 *
 * Utility to call the edge function for updating onboarding status
 * at the organization and location level.
 *
 * Uses VITE_MAIN_APP_SUPABASE_SERVICE_ROLE_KEY for authentication.
 *
 * NOTE: Maps onboarding portal status to main app status values:
 * - PENDING_REVIEW → pending_approval
 * - APPROVED → awaiting_config (or awaiting_payment if payment needed)
 * - PAYMENT_RECEIVED → awaiting_it_config
 * - DATA_CONFIGURATION → awaiting_data_config
 * - FINAL_REVIEW → awaiting_activation
 * - ACTIVATED → active
 * - REJECTED → rejected_review
 */

import type { OrganizationStatus } from "@/types/database";
import { createClient } from "./supabase";

export interface LocationStatusUpdate {
  id: string;
  status: string;
}

export interface UpdateOnboardingStatusRequest {
  organization: {
    id: string; // Organization ID to update
    status: string; // Main app status values
  };
  locations: LocationStatusUpdate[]; // Locations array with IDs
}

export interface UpdateOnboardingStatusResponse {
  success: boolean;
  message?: string;
  error?: string;
  timestamp: string;
}

/**
 * Map onboarding portal status to main app status values
 */
function mapStatusToMainApp(portalStatus: OrganizationStatus | string): string {
  const statusMap: Record<string, string> = {
    PENDING_REVIEW: "PENDING_APPROVAL",
    APPROVED: "AWAITING_CONFIG",
    AWAITING_PAYMENT: "AWAITING_PAYMENT",
    AWAITING_CONFIG: "AWAITING_CONFIG",
    DATA_CONFIGURATION: "AWAITING_DATA_CONFIG",
    FINAL_REVIEW: "AWAITING_ACTIVATION",
    AWAITING_ACTIVATION: "AWAITING_ACTIVATION",
    ACTIVATED: "ACTIVE",
    REJECTED_REVIEW: "REJECTED_REVIEW",
  };

  return statusMap[portalStatus as string] || portalStatus;
}

/**
 * Call the update-onboarding-status edge function
 *
 * This function updates the onboarding status for an organization and its locations.
 * Used when approval/rejection happens, payment is received, or tasks are completed.
 *
 * @param request - The update request containing organization and location status updates
 * @returns The response from the edge function
 * @throws Error if the request fails
 */
export async function updateOnboardingStatus(
  request: UpdateOnboardingStatusRequest
): Promise<UpdateOnboardingStatusResponse> {
  try {
    const mainAppUrl = import.meta.env.VITE_SUPABASE_MAIN_APP_URL;
    const serviceRoleKey = import.meta.env
      .VITE_MAIN_APP_SUPABASE_SERVICE_ROLE_KEY;

    if (!mainAppUrl) {
      throw new Error("VITE_SUPABASE_MAIN_APP_URL is not configured");
    }

    if (!serviceRoleKey) {
      throw new Error(
        "VITE_MAIN_APP_SUPABASE_SERVICE_ROLE_KEY is not configured"
      );
    }

    // Map status to main app values
    const mappedRequest: UpdateOnboardingStatusRequest = {
      ...request,
      organization: {
        ...request.organization,
        status: mapStatusToMainApp(request.organization.status),
      },
    };

    console.log("Updating onboarding status:", {
      original: request.organization.status,
      mapped: mappedRequest.organization.status,
      locationCount: mappedRequest.locations.length,
    });

    const response = await fetch(
      `${mainAppUrl}/functions/v1/update-onboarding-status`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${serviceRoleKey}`,
        },
        body: JSON.stringify(mappedRequest),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP ${response.status}`;
      try {
        const errorData = JSON.parse(errorText);
        errorMessage =
          errorData.error || errorData.details || errorText || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      console.error("Status update error response:", {
        status: response.status,
        contentType: response.headers.get("content-type"),
        body: errorText,
      });
      throw new Error(`Failed to update onboarding status: ${errorMessage}`);
    }

    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      const data = await response.json();
      return data;
    } else {
      // Handle non-JSON responses
      const text = await response.text();
      console.warn("Non-JSON response from status update:", {
        contentType,
        body: text,
      });
      return {
        success: true,
        message: "Status updated (non-JSON response)",
        timestamp: new Date().toISOString(),
      };
    }
  } catch (error) {
    console.error("Error updating onboarding status:", error);
    throw error;
  }
}

/**
 * Update organization status only (without locations)
 *
 * @param organizationId - The organization ID
 * @param status - The new organization status
 */
export async function updateOrganizationStatus(
  organizationId: string,
  status: OrganizationStatus
): Promise<UpdateOnboardingStatusResponse> {
  return updateOnboardingStatus({
    organization: {
      id: organizationId,
      status,
    },
    locations: [],
  });
}

/**
 * Update location statuses for an organization
 *
 * @param organizationId - The organization ID
 * @param organizationStatus - The organization status
 * @param locations - Array of locations with their statuses
 */
export async function updateLocationStatuses(
  organizationId: string,
  organizationStatus: OrganizationStatus,
  locations: LocationStatusUpdate[]
): Promise<UpdateOnboardingStatusResponse> {
  return updateOnboardingStatus({
    organization: {
      id: organizationId,
      status: organizationStatus,
    },
    locations,
  });
}


/**
 * Update deactivation status for locations in the main app
 *
 * @param request - The deactivation status update request
 * @returns The response from the edge function
 * @throws Error if the request fails
 */
export interface UpdateDeactivationStatusRequest {
  organization_id: string;
  locations: Array<{
    location_id: string;
    status: string;
    approved_by?: string;
    disapproved_by?: string;
    disapproval_reason?: string;
  }>;
}

export interface UpdateDeactivationStatusResponse {
  success: boolean;
  updated: number;
  failed: number;
  results: Array<{
    location_id: string;
    status: string;
    success: boolean;
    error?: string;
  }>;
}

export async function updateDeactivationStatus(
  request: UpdateDeactivationStatusRequest
): Promise<UpdateDeactivationStatusResponse> {
  try {
    const mainAppUrl = import.meta.env.VITE_SUPABASE_MAIN_APP_URL;
    const serviceRoleKey = import.meta.env
      .VITE_MAIN_APP_SUPABASE_SERVICE_ROLE_KEY;

    if (!mainAppUrl) {
      throw new Error("VITE_SUPABASE_MAIN_APP_URL is not configured");
    }

    if (!serviceRoleKey) {
      throw new Error(
        "VITE_MAIN_APP_SUPABASE_SERVICE_ROLE_KEY is not configured"
      );
    }

    console.log("Updating deactivation status in main app:", {
      organization_id: request.organization_id,
      location_count: request.locations.length,
      statuses: request.locations.map((l) => l.status),
    });

    const response = await fetch(
      `${mainAppUrl}/functions/v1/update-deactivation-status`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${serviceRoleKey}`,
        },
        body: JSON.stringify(request),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP ${response.status}`;
      try {
        const errorData = JSON.parse(errorText);
        errorMessage =
          errorData.error || errorData.details || errorText || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      console.error("Deactivation status update error response:", {
        status: response.status,
        contentType: response.headers.get("content-type"),
        body: errorText,
      });
      throw new Error(`Failed to update deactivation status: ${errorMessage}`);
    }

    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      const data = await response.json();
      return data;
    } else {
      // Handle non-JSON responses
      const text = await response.text();
      console.warn("Non-JSON response from deactivation status update:", {
        contentType,
        body: text,
      });
      return {
        success: true,
        updated: request.locations.length,
        failed: 0,
        results: request.locations.map((l) => ({
          location_id: l.location_id,
          status: l.status,
          success: true,
        })),
      };
    }
  } catch (error) {
    console.error("Error updating deactivation status:", error);
    throw error;
  }
}

/**
 * Mark client meeting as done for a location in the main app
 *
 * @param locationId - The location ID
 * @param meetingDone - Whether the meeting is done
 * @returns The response from the edge function
 * @throws Error if the request fails
 */
export async function markClientMeetingDone(
  locationId: string,
  meetingDone: boolean
): Promise<{ success: boolean; message?: string; error?: string }> {
  try {
    const mainAppUrl = import.meta.env.VITE_SUPABASE_MAIN_APP_URL;
    const serviceRoleKey = import.meta.env
      .VITE_MAIN_APP_SUPABASE_SERVICE_ROLE_KEY;

    if (!mainAppUrl) {
      throw new Error("VITE_SUPABASE_MAIN_APP_URL is not configured");
    }

    if (!serviceRoleKey) {
      throw new Error(
        "VITE_MAIN_APP_SUPABASE_SERVICE_ROLE_KEY is not configured"
      );
    }

    console.log("Marking client meeting done:", {
      location_id: locationId,
      meeting_done: meetingDone,
    });

    const response = await fetch(
      `${mainAppUrl}/functions/v1/mark-meeting-done`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${serviceRoleKey}`,
        },
        body: JSON.stringify({
          location_id: locationId,
          meeting_done: meetingDone,
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP ${response.status}`;
      try {
        const errorData = JSON.parse(errorText);
        errorMessage =
          errorData.error || errorData.details || errorText || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      console.error("Mark meeting done error response:", {
        status: response.status,
        contentType: response.headers.get("content-type"),
        body: errorText,
      });
      throw new Error(`Failed to mark meeting as done: ${errorMessage}`);
    }

    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      return await response.json();
    } else {
      const text = await response.text();
      console.warn("Non-JSON response from mark-meeting-done:", {
        contentType,
        body: text,
      });
      return { success: true, message: "Meeting marked as done" };
    }
  } catch (error) {
    console.error("Error marking meeting done:", error);
    throw error;
  }
}

// ── Archive / Reactivate ──────────────────────────────────────────────────

export interface ArchiveOrganizationResponse {
  success: boolean;
  immediate_count?: number;
  pending_count?: number;
  total_locations?: number;
  message?: string;
  error?: string;
}

export interface ReactivateOrganizationResponse {
  success: boolean;
  restored_locations?: number;
  subscriptions_resumed?: number;
  new_subscriptions_created?: number;
  failed_locations?: Array<{ location_id: string; location_name: string; reason: string }>;
  error?: string;
}

/**
 * Initiate an org archive.
 * Sets the org to archive_pending and schedules Stripe cancel_at_period_end
 * for all active location subscriptions.
 *
 * @param organizationId - The main-app organization ID (UUID)
 * @param requestedBy    - The ops portal user ID performing the action (for audit trail)
 */
export async function archiveOrganization(
  organizationId: string,
  requestedBy: string
): Promise<ArchiveOrganizationResponse> {
  let lockAcquired = false;
  try {
    await acquireLock(organizationId);
    lockAcquired = true;

    const mainAppUrl = import.meta.env.VITE_SUPABASE_MAIN_APP_URL;
    const serviceRoleKey = import.meta.env.VITE_MAIN_APP_SUPABASE_SERVICE_ROLE_KEY;

    if (!mainAppUrl) throw new Error("VITE_SUPABASE_MAIN_APP_URL is not configured");
    if (!serviceRoleKey) throw new Error("VITE_MAIN_APP_SUPABASE_SERVICE_ROLE_KEY is not configured");

    const response = await fetch(
      `${mainAppUrl}/functions/v1/archive-organization`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${serviceRoleKey}`,
        },
        body: JSON.stringify({ organization_id: organizationId, requested_by: requestedBy }),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error ?? `HTTP ${response.status}`);
    }

    return data;
  } finally {
    if (lockAcquired) {
      await releaseLock(organizationId);
    }
  }
}

/**
 * Reactivate a fully-archived or archive-pending organization.
 * Restores all archived locations and creates new Stripe subscriptions where needed.
 *
 * @param organizationId  - The main-app organization ID (UUID)
 * @param reactivatedBy   - The ops portal user ID performing the action (for audit trail)
 */
export async function reactivateOrganization(
  organizationId: string,
  reactivatedBy: string,
  locationIds?: string[]
): Promise<ReactivateOrganizationResponse> {
  let lockAcquired = false;
  try {
    await acquireLock(organizationId);
    lockAcquired = true;

    const mainAppUrl = import.meta.env.VITE_SUPABASE_MAIN_APP_URL;
    const serviceRoleKey = import.meta.env.VITE_MAIN_APP_SUPABASE_SERVICE_ROLE_KEY;

    if (!mainAppUrl) throw new Error("VITE_SUPABASE_MAIN_APP_URL is not configured");
    if (!serviceRoleKey) throw new Error("VITE_MAIN_APP_SUPABASE_SERVICE_ROLE_KEY is not configured");

    const response = await fetch(
      `${mainAppUrl}/functions/v1/reactivate-organization`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${serviceRoleKey}`,
        },
        body: JSON.stringify({
          organization_id: organizationId,
          reactivated_by: reactivatedBy,
          ...(locationIds && { location_ids: locationIds }),
        }),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error ?? `HTTP ${response.status}`);
    }

    return data;
  } finally {
    if (lockAcquired) {
      await releaseLock(organizationId);
    }
  }
}

/**
 * Delete an archived location permanently.
 * Sets deleted_at, cancels any remaining Stripe subscription.
 */
export async function deleteArchivedLocation(
  organizationId: string,
  locationId: string,
  deletedBy: string
): Promise<{ success: boolean; error?: string }> {
  let lockAcquired = false;
  try {
    await acquireLock(organizationId);
    lockAcquired = true;

    const mainAppUrl = import.meta.env.VITE_SUPABASE_MAIN_APP_URL;
    const serviceRoleKey = import.meta.env.VITE_MAIN_APP_SUPABASE_SERVICE_ROLE_KEY;

    if (!mainAppUrl) throw new Error("VITE_SUPABASE_MAIN_APP_URL is not configured");
    if (!serviceRoleKey) throw new Error("VITE_MAIN_APP_SUPABASE_SERVICE_ROLE_KEY is not configured");

    const response = await fetch(
      `${mainAppUrl}/functions/v1/delete-archived-location`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${serviceRoleKey}`,
        },
        body: JSON.stringify({
          organization_id: organizationId,
          location_id: locationId,
          deleted_by: deletedBy,
        }),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error ?? `HTTP ${response.status}`);
    }

    return data;
  } finally {
    if (lockAcquired) {
      await releaseLock(organizationId);
    }
  }
}

/**
 * Process activation for a location (and organization if needed) via edge function
 *
 * @param organizationId - The organization ID
 * @param locationId - The location ID
 * @param userId - The user ID performing the action (for logging)
 */
// Helper to check and acquire lock
export async function acquireLock(organizationId: string) {
  const supabase = createClient();
  
  // 1. Check current lock
  const { data: org, error: fetchError } = await supabase
    .from('organizations')
    .select('locked_at')
    .eq('id', organizationId)
    .single();

  if (fetchError) throw fetchError;

  const orgData = org as any; // Cast to access new column

  if (orgData?.locked_at) {
    const lockedTime = new Date(orgData.locked_at).getTime();
    const now = new Date().getTime();
    const lockGracePeriod = 5 * 60 * 1000; // 5 minutes

    if (now - lockedTime < lockGracePeriod) {
      throw new Error('Organization is currently being processed. Please try again in 5 minutes.');
    }
  }

  // 2. Acquire lock
  const { error: lockError } = await supabase
    .from('organizations')
    .update({ locked_at: new Date().toISOString() } as any)
    .eq('id', organizationId);

  if (lockError) throw new Error(`Failed to acquire lock: ${lockError.message}`);
}

// Helper to release lock
export async function releaseLock(organizationId: string) {
  const supabase = createClient();
  console.log(`Releasing lock for organization ${organizationId}`);
  
  const { error } = await supabase
    .from('organizations')
    .update({ locked_at: null } as any)
    .eq('id', organizationId);
    
  if (error) {
    console.error('Failed to release lock:', error);
    // We log but don't throw, as the main operation might have succeeded
  }
}

export async function processActivation(
  organizationId: string,
  locationId: string,
  userId?: string
) {
  let lockAcquired = false;

  try {
    // 1. Acquire Lock
    console.log(`Acquiring lock for organization ${organizationId} before activation...`);
    await acquireLock(organizationId);
    lockAcquired = true;

    // 2. Call Edge Function
    const supabase = createClient();
    const { data: responseData, error } = await supabase.functions.invoke(
      'process-activation',
      {
        body: {
          organization_id: organizationId,
          location_id: locationId,
          user_id: userId,
        },
      }
    );

    if (error) {
      const errorBody = await error.context?.json();
      throw new Error(
        errorBody?.error || error.message || 'Failed to process activation'
      );
    }

    return responseData;

  } catch (error) {
    console.error('Error in processActivation:', error);
    throw error;
  } finally {
    // 3. Release Lock (always)
    if (lockAcquired) {
      await releaseLock(organizationId);
    }
  }
}
